import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Video, Play, Calendar, ExternalLink, MoreVertical } from "lucide-react";
import {
  VentureCard,
  VentureCardContent,
  VentureCardHeader,
  VentureCardTitle,
} from "@/components/ui/venture-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Row {
  id: string;
  title: string | null;
  fathom_share_url: string | null;
  duration_seconds: number | null;
  recorded_at: string;
  demo_id: string | null;
  client_id: string | null;
  closer_id: string;
  demos: {
    prospect_name: string;
    demo_date: string;
    status: string | null;
    outcome: string | null;
  } | null;
  clients: { id: string; company_name: string } | null;
}

interface AssignedClient {
  id: string;
  company_name: string;
}

interface DemoRow {
  id: string;
  demo_date: string;
  status: string | null;
  client_id: string;
  closer_id: string;
  prospect_name?: string;
}

function fmtDuration(s: number | null): string {
  if (!s || s <= 0) return "--:--";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

// ---------------- Closer-scoped picker hooks ----------------

function useMyClients() {
  return useQuery<AssignedClient[]>({
    queryKey: ["closer-assignments-self"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("closer_assignments")
        .select("client_id, clients(id, company_name)")
        .eq("status", "active");
      if (error) throw error;
      return (data ?? [])
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((row: any) => row.clients)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((c: any) => c != null)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .sort((a: any, b: any) =>
          (a.company_name ?? "").localeCompare(b.company_name ?? ""),
        );
    },
    staleTime: 60_000,
  });
}

function useMyDemosForClient(clientId: string | null) {
  return useQuery<DemoRow[]>({
    queryKey: ["my-demos-for-client", clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from("demos")
        .select("id, demo_date, status, client_id, closer_id, prospect_name")
        .eq("client_id", clientId)
        .order("demo_date", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as DemoRow[];
    },
    enabled: !!clientId,
    staleTime: 30_000,
  });
}

// ---------------- Orphan mini resolver ----------------

function CloserOrphanResolver({
  rec,
  myClients,
}: {
  rec: Row;
  myClients: AssignedClient[];
}) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState("");
  const [pickedClientId, setPickedClientId] = useState<string | null>(null);
  const { data: demos } = useMyDemosForClient(pickedClientId);

  const filtered = useMemo(
    () =>
      myClients.filter((c) =>
        c.company_name.toLowerCase().includes(filter.toLowerCase()),
      ),
    [myClients, filter],
  );

  async function linkToDemo(d: DemoRow) {
    try {
      const { error } = await supabase.functions.invoke('resolve-recording', {
        body: {
          recording_id: rec.id,
          action: 'link',
          demo_id: d.id,
          client_id: pickedClientId,
        },
      });
      if (error) throw error;
      toast.success("Recording linked to demo");
      qc.invalidateQueries({ queryKey: ["closer-demo-recordings"] });
      setExpanded(false);
    } catch {
      toast.error("Failed to link recording");
    }
  }

  async function linkToClientOnly() {
    try {
      const { error } = await supabase.functions.invoke('resolve-recording', {
        body: {
          recording_id: rec.id,
          action: 'link',
          demo_id: null,
          client_id: pickedClientId,
        },
      });
      if (error) throw error;
      toast.success("Recording linked to client");
      qc.invalidateQueries({ queryKey: ["closer-demo-recordings"] });
      setExpanded(false);
    } catch {
      toast.error("Failed to link recording");
    }
  }

  if (!expanded) {
    return (
      <Button
        size="sm"
        variant="outline"
        className="w-full"
        onClick={() => setExpanded(true)}
      >
        Resolve
      </Button>
    );
  }

  return (
    <div className="space-y-2 border border-border rounded p-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium">Link this recording</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setExpanded(false);
            setPickedClientId(null);
            setFilter("");
          }}
        >
          Cancel
        </Button>
      </div>

      {!pickedClientId ? (
        <div className="space-y-2">
          <Input
            placeholder="Search clients…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className="max-h-40 overflow-y-auto space-y-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-muted-foreground px-2">
                No assigned clients found.
              </p>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-surface"
                  onClick={() => setPickedClientId(c.id)}
                >
                  {c.company_name}
                </button>
              ))
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span>
              Client:{" "}
              <strong>
                {myClients.find((c) => c.id === pickedClientId)?.company_name ??
                  "Unknown"}
              </strong>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPickedClientId(null)}
            >
              Change
            </Button>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {(demos ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground px-2">
                No demos found for this client.
              </p>
            ) : (
              (demos ?? []).map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-surface"
                  onClick={() => linkToDemo(d)}
                >
                  {d.prospect_name ?? d.id} —{" "}
                  {format(new Date(d.demo_date), "MMM d")} (
                  {d.status ?? "unknown"})
                </button>
              ))
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            onClick={linkToClientOnly}
          >
            Or link to client only — no demo row
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------- Kebab reassign/unlink for linked cards ----------------

function CloserRowKebab({
  rec,
  myClients,
}: {
  rec: Row;
  myClients: AssignedClient[];
}) {
  const qc = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState<
    null | "reassign-client" | "reassign-demo" | "confirm-unlink"
  >(null);
  const [filter, setFilter] = useState("");
  const { data: demos } = useMyDemosForClient(
    mode === "reassign-demo" ? rec.client_id : null,
  );

  const filteredClients = useMemo(
    () =>
      myClients.filter((c) =>
        c.company_name.toLowerCase().includes(filter.toLowerCase()),
      ),
    [myClients, filter],
  );

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["closer-demo-recordings"] });
  }

  async function reassignClient(clientId: string, company_name: string) {
    try {
      const { data, error } = await supabase.functions.invoke(
        'resolve-recording',
        {
          body: {
            recording_id: rec.id,
            action: 'reassign',
            client_id: clientId,
          },
        },
      );
      if (error) throw error;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cleared = (data as any)?.cleared_demo_id === true;
      if (cleared) {
        toast.info(
          `Recording reassigned to ${company_name}. Previous demo link cleared.`,
        );
      } else {
        toast.success("Recording reassigned");
      }
      invalidate();
      setMode(null);
    } catch {
      toast.error("Failed to reassign recording");
    }
  }

  async function reassignDemo(demoId: string) {
    try {
      const { error } = await supabase.functions.invoke('resolve-recording', {
        body: {
          recording_id: rec.id,
          action: 'reassign',
          demo_id: demoId,
        },
      });
      if (error) throw error;
      toast.success("Recording re-linked to demo");
      invalidate();
      setMode(null);
    } catch {
      toast.error("Failed to reassign recording");
    }
  }

  async function doUnlink() {
    try {
      const { error } = await supabase.functions.invoke('resolve-recording', {
        body: { recording_id: rec.id, action: 'unlink' },
      });
      if (error) throw error;
      toast.success("Recording unlinked");
      invalidate();
      setMode(null);
    } catch {
      toast.error("Failed to unlink recording");
    }
  }

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        aria-label="More actions"
        title="More actions"
        onClick={() => setMenuOpen((o) => !o)}
      >
        <MoreVertical className="w-4 h-4" />
      </Button>
      {menuOpen && (
        <div
          className="absolute right-0 top-full mt-1 z-10 w-44 rounded-md border border-border bg-background shadow-md p-1"
          role="menu"
        >
          <button
            type="button"
            role="menuitem"
            className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-surface"
            onClick={() => {
              setMode("reassign-client");
              setMenuOpen(false);
            }}
          >
            Reassign client…
          </button>
          <button
            type="button"
            role="menuitem"
            className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-surface"
            onClick={() => {
              setMode("reassign-demo");
              setMenuOpen(false);
            }}
          >
            Reassign demo…
          </button>
          <div className="h-px bg-border my-1" />
          <button
            type="button"
            role="menuitem"
            className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-surface"
            onClick={() => {
              setMode("confirm-unlink");
              setMenuOpen(false);
            }}
          >
            Unlink
          </button>
        </div>
      )}

      {mode === "reassign-client" && (
        <div className="mt-2 space-y-2 border border-border rounded p-2">
          <Input
            placeholder="Search clients…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className="max-h-40 overflow-y-auto space-y-1">
            {filteredClients.map((c) => (
              <button
                key={c.id}
                type="button"
                className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-surface"
                onClick={() => reassignClient(c.id, c.company_name)}
              >
                {c.company_name}
              </button>
            ))}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => setMode(null)}
          >
            Cancel
          </Button>
        </div>
      )}

      {mode === "reassign-demo" && (
        <div className="mt-2 space-y-2 border border-border rounded p-2">
          <div className="text-xs text-muted-foreground">Pick a demo:</div>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {(demos ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground px-2">
                No demos found for this client.
              </p>
            ) : (
              (demos ?? []).map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-surface"
                  onClick={() => reassignDemo(d.id)}
                >
                  {d.prospect_name ?? d.id} —{" "}
                  {format(new Date(d.demo_date), "MMM d")} (
                  {d.status ?? "unknown"})
                </button>
              ))
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => setMode(null)}
          >
            Cancel
          </Button>
        </div>
      )}

      {mode === "confirm-unlink" && (
        <div className="mt-2 space-y-2 border border-border rounded p-2">
          <p className="text-xs">Unlink this recording?</p>
          <div className="flex gap-2">
            <Button size="sm" variant="danger" onClick={doUnlink}>
              Yes
            </Button>
            <Button size="sm" variant="outline" onClick={() => setMode(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Page ----------------

export default function DemoLibrary() {
  const { data: myClients } = useMyClients();

  // RLS auto-scopes to rows tied to this closer (closers.user_id = auth.uid()).
  const { data: recordings, isLoading } = useQuery({
    queryKey: ["closer-demo-recordings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("demo_recordings")
        .select(
          "id, title, fathom_share_url, duration_seconds, recorded_at, demo_id, client_id, closer_id, demos(prospect_name, demo_date, status, outcome), clients(id, company_name)",
        )
        .order("recorded_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Row[];
    },
    staleTime: 60_000,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Video className="w-6 h-6 text-secondary" />
        <h1 className="text-2xl font-bold">My Demo Library</h1>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : (recordings ?? []).length === 0 ? (
        <VentureCard>
          <VentureCardContent className="py-10 text-center">
            <Video className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">
              No demo recordings yet. Connect Fathom and your demos will appear here.
            </p>
          </VentureCardContent>
        </VentureCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(recordings ?? []).map((r) => {
            const isOrphan = r.demo_id === null && r.client_id === null;
            return (
              <VentureCard key={r.id}>
                <VentureCardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <VentureCardTitle className="text-base">
                      {r.title || "Demo Recording"}
                    </VentureCardTitle>
                    {!isOrphan && (
                      <CloserRowKebab rec={r} myClients={myClients ?? []} />
                    )}
                  </div>
                </VentureCardHeader>
                <VentureCardContent className="space-y-2">
                  {r.demos && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {r.demos.prospect_name} —{" "}
                        {format(new Date(r.demos.demo_date), "MMM d, yyyy")}
                      </span>
                    </div>
                  )}
                  {r.clients?.company_name && (
                    <div className="text-xs text-muted-foreground">
                      Client: {r.clients.company_name}
                    </div>
                  )}
                  {isOrphan && (
                    <div className="text-xs text-muted-foreground italic">
                      Unassigned
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    Recorded{" "}
                    {format(new Date(r.recorded_at), "MMM d, yyyy 'at' h:mm a")}
                    {" · "}
                    {fmtDuration(r.duration_seconds)}
                  </div>
                  {r.fathom_share_url ? (
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        className="flex-1 gap-2"
                        onClick={() =>
                          window.open(r.fathom_share_url!, "_blank", "noopener")
                        }
                      >
                        <Play className="w-4 h-4" /> Watch
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          window.open(r.fathom_share_url!, "_blank", "noopener")
                        }
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" className="w-full" disabled>
                      Processing…
                    </Button>
                  )}
                  {isOrphan && (
                    <CloserOrphanResolver rec={r} myClients={myClients ?? []} />
                  )}
                </VentureCardContent>
              </VentureCard>
            );
          })}
        </div>
      )}
    </div>
  );
}
